import { memo } from "react";

import { Heading } from "@components/Heading";
import { Image } from "@components/Image";
import { Text } from "@components/Text";
import { type ProductValueProp } from "@tablescape/state";
import { formatFullyQualifiedUrl } from "@tablescape/utilities";
import { tw } from "classnames";

type Props = {
  lazyLoadImages: boolean;
  productValueProps: ProductValueProp[];
};

const styles = {
  base: tw(
    "col-span-2",
    "w-full",
    "justify-center",
    "bg-background-secondary",
    "mg:flex",
    "mg:py-9"
  ),
  item: (index: number) =>
    tw(
      "mx-4",
      "box-border",
      "py-6",
      "text-center",
      "text-primary",
      "mg:mx-0",
      "mg:w-1/2",
      "mg:px-8",
      "mg:py-0",
      {
        "border-0": index === 1,
        "border-t": index === 1,
        "border-solid": index === 1,
        "border-divider": index === 1,
        "mg:border-l": index === 1,
        "mg:border-t-0": index === 1,
      }
    ),
  image: tw(
    "mx-auto",
    "mb-2",
    "w-8",
    "shrink-0",
    "mg:mx-0",
    "mg:mb-0",
    "mg:mr-2.5"
  ),
  titleImageContainer: tw(
    "mb-2.5",
    "items-center",
    "justify-center",
    "mg:flex"
  ),
  text: tw("mt-2"),
};

const ProductValuePropsViewBase = ({
  lazyLoadImages,
  productValueProps,
}: Props) => {
  return (
    <div className={styles.base}>
      {productValueProps.map((productValueProp, index) => (
        <div
          className={styles.item(index)}
          key={`productValueProp-${productValueProp.title}`}
        >
          <div className={styles.titleImageContainer}>
            {productValueProp.image && (
              <div className={styles.image}>
                <Image
                  alt={productValueProp.image.altText}
                  height={32}
                  loading={lazyLoadImages ? "lazy" : "eager"}
                  src={formatFullyQualifiedUrl(productValueProp.image.url)}
                  width={32}
                />
              </div>
            )}
            <Heading as="h6" size="xs">
              {productValueProp.title}
            </Heading>
          </div>
          <Text className={styles.text}>{productValueProp.text}</Text>
        </div>
      ))}
    </div>
  );
};

export const ProductValuePropsView = memo(ProductValuePropsViewBase);
